import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from './ui/button';
import { ShoppingCart, BookOpen, Sparkles } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const HeroSection = () => {
  const { books } = useSiteData();
  const bookData = books[0] || {};
  const [mounted, setMounted] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const textLines = [
    { text: "एक पुस्तक...", delay: 0.2 },
    { text: "एक विचार...", delay: 0.5 },
    { text: "एक संकल्प...", delay: 0.8 },
    { text: "विकसित भारत 2047 की ओर", delay: 1.1 }
  ];

  return (
    <motion.section 
      style={{ opacity }}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-50 via-white to-blue-50"
    >
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-900/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content - Text */}
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Kinetic Title with Masked Line-by-Line Reveal */}
            <div className="space-y-3">
              {mounted && textLines.map((line, index) => (
                <motion.h1
                  key={index}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-orange-500 to-orange-700"
                  style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                  initial={{ opacity: 0, y: 20, clipPath: 'inset(0 100% 0 0)' }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    clipPath: 'inset(0 0% 0 0)'
                  }}
                  transition={{ 
                    duration: 0.8, 
                    delay: line.delay,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  {line.text}
                </motion.h1>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.6 }}
              className="space-y-4"
            >
              <h2 
                className="text-2xl md:text-3xl font-semibold text-blue-900"
                style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
              >
                {bookData.title}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed max-w-xl">
                {bookData.description}
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.6 }}
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                onClick={() => window.open(bookData.amazonLink, '_blank')}
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                अभी खरीदें
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white transition-all duration-300 hover:scale-105"
                onClick={() => document.getElementById('book-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <BookOpen className="mr-2 h-5 w-5" />
                अधिक जानें
              </Button>
            </motion.div>

            {/* Sparkle Element */}
            <motion.div
              className="flex items-center gap-2 text-orange-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.1, duration: 0.6 }}
            >
              <Sparkles className="h-5 w-5 animate-pulse" />
              <span className="text-sm font-medium" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>भारत के प्रमुख प्लेटफॉर्म पर उपलब्ध</span>
            </motion.div>
          </motion.div>

          {/* Right Content - 3D Book */}
          <motion.div 
            style={{ y }}
            className="relative flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.8, rotateY: -30 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.div
              className="relative w-full max-w-md"
              whileHover={{ 
                scale: 1.05,
                rotateY: 5,
                rotateX: 5,
                transition: { duration: 0.3 }
              }}
              style={{ perspective: 1000 }}
            >
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/50 to-blue-600/50 blur-3xl opacity-60 animate-pulse" />
              
              {/* Book Image with 3D Effect */}
              <motion.div
                className="relative rounded-2xl overflow-hidden shadow-2xl"
                animate={{
                  rotateY: [0, 5, 0, -5, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <img 
                  src={bookData.coverImage}
                  alt={bookData.title}
                  className="w-full h-auto object-cover"
                />
                
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 to-transparent" />
              </motion.div>

              {/* Floating Badge */}
              <motion.div
                className="absolute -top-4 -right-4 bg-orange-500 text-white px-6 py-3 rounded-full shadow-lg font-bold"
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                {bookData.price}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <div className="w-6 h-10 border-2 border-orange-500 rounded-full flex items-start justify-center p-2">
          <motion.div 
            className="w-1.5 h-1.5 bg-orange-500 rounded-full"
            animate={{
              y: [0, 16, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      </motion.div>
    </motion.section>
  );
};

export default HeroSection;